import { Component, AfterViewInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { loadStripe, Stripe, StripeElements, StripeElementsOptionsMode } from '@stripe/stripe-js';
import { StripePaymentService } from 'src/app/services/stripe-payment.service';
import { StoreService } from 'src/app/services/store.service';
import { Observable } from 'rxjs';
import { pages } from 'src/app/alv-enums';
declare var $: any

@Component({
   selector: 'stripe-payment',
   templateUrl: './stripe-payment.component.html',
   styleUrls: ['./stripe-payment.component.scss']
})

export class StripePaymentComponent implements AfterViewInit {
   toolsService: any;
   useAddressVerification: any;

   constructor(
      private http: HttpClient,
      private stripeService: StripePaymentService,
      public storeService: StoreService
   ) { }

   public currency = 'usd';
   public loading = false;
   public message: string | null = null;
   public error = false;

   private stripe: Stripe | null = null;
   private elements: StripeElements | null = null;
   private clientSecret: string | null = null;
   @Output() private paymentDone = new EventEmitter<pages>();
   @Output() private cardOptionChange = new EventEmitter<string>();

   async ngAfterViewInit() {
      try {
         this.storeService.loading = true;
         // Reverted to .toPromise() for compatibility with older RxJS versions
         const config = await this.stripeService.getStripePaymentConfiguration().toPromise();

         // Load Stripe.js with the publishable key from your backend configuration
         this.stripe = await loadStripe(config.PublishableKey, {
            stripeAccount: config.AccountId || undefined,
         });

         this.currency = config.Currency || 'usd';
         this.useAddressVerification = config.UseAddressVerification || false;

         if (!this.stripe) {
            this.message = 'Failed to load Stripe. Please check your network connection.';
            this.error = true;
            this.storeService.loading = false;
            return;
         }

         await this.createPaymentIntentAndMountElement();

      } catch (e) {
         this.message = e.message || 'An error occurred while loading the payment configuration.';
         this.error = true;
         this.storeService.loading = false;
         console.error(e);
      }
   }

   private async createPaymentIntentAndMountElement() {
  try {
    const options: StripeElementsOptionsMode = {
      mode: 'payment',
      amount: Math.round(this.storeService.shoppingCart.total * 100),
      currency: this.currency,
      captureMethod: 'manual'
    };

    this.elements = this.stripe.elements(options);
    const paymentElement = this.elements.create('payment');
    paymentElement.mount('#payment-element');

    if (this.useAddressVerification) {
      // Wait for Angular to render the address-element div
      setTimeout(() => {
        const addressDiv = document.getElementById('address-element');
        if (addressDiv) {
          const elementsAny: any = this.elements;
          const addressElement = elementsAny.create('address', { mode: 'billing' });
          addressElement.mount('#address-element');
        }
      }, 0);
    }
    this.storeService.loading = false;

  } catch (e) {
    this.message = e.message || 'An error occurred while setting up the payment form.';
    this.error = true;
    console.error(e);
    this.storeService.loading = false;
  }
}

   async handleSubmit() {
      this.storeService.loading = true;
      this.message = null;
      const { error: submitError } = await this.elements.submit();
      if (submitError) {
         this.storeService.loading = false;
      }
      else {

         let guestName = this.storeService.shoppingCart.guestName;
         let guestEmail = this.storeService.shoppingCart.guestEmail;
         let locationId = this.storeService.kioskSettings.locationId || 1;

         let customer = await this.stripeService.getCustomer(guestName, guestEmail, locationId).toPromise();

         const paymentAmount = this.storeService.shoppingCart.total;

         const createdPaymentIntent = await this.stripeService.createPaymentIntent(paymentAmount, this.currency, true, customer.customerId, locationId).toPromise();
         this.clientSecret = createdPaymentIntent.clientSecret;
         // Confirm the payment with Stripe.
         const { error, paymentIntent } = await this.stripe.confirmPayment({
            elements: this.elements,
            clientSecret: this.clientSecret,
            confirmParams: {
               return_url: window.location.origin
            },
            redirect: 'if_required'
         });

         if (error) {
            // Handle payment confirmation errors.
            this.message = error.message || 'Payment failed.';
            this.error = true;
         } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
            try {
               const receiptInfo = await this.stripeService.savePaymentResult(paymentIntent.id, this.storeService.shoppingCart.total).toPromise();
               if (receiptInfo) {
                  this.storeService.shoppingCart.transactionSourceType = receiptInfo.transactionSourceType;
                  this.storeService.shoppingCart.transactionSourceTypeId = receiptInfo.transactionSourceTypeId;
                  this.storeService.shoppingCart.isTicketType = receiptInfo.isTicketType;
                  this.storeService.shoppingCart.isContainsPdf = receiptInfo.isContainsPdf;
               }
               this.paymentDone.emit();
               this.message = 'Payment successful! Your order is being processed.';
               this.error = false;
               this.storeService.loading = false;
            } catch (backendError) {
               this.message = backendError.message || 'Payment was successful, but there was an issue processing your order on our side.';
               this.error = true;
               console.error('Backend processing error:', backendError);
               this.storeService.loading = false;
            }
         } else {
            // Fallback for any other unexpected status.
            this.message = 'An unexpected payment status was received. Please contact support.';
            this.error = true;
            this.storeService.loading = false;
         }
         this.storeService.loading = false;
      }
   }

   public onCardSwipe() {
      this.cardOptionChange.emit('swipe');
   }

   public onBack() {
      this.cardOptionChange.emit('back');
   }
}
